import { select, takeEvery, spawn, take } from 'redux-saga/effects'
import { sendPostMessage } from 'utils'
import { eventFields, formatDate } from 'utils/eventFields'

import {
  routerMatch as routerMatchActions,
  tracking as trackingActions,
  tags as tagsActions,
  viewer as viewerActions,
  retailerCart as retailerCartActions,
} from 'flux/actions'

import {
  property as propertySelectors,
  flyers as flyersSelectors,
  tracking as trackingSelectors,
  tags as tagsSelectors,
  flyerGibs as flyerGibsSelectors,
  ui as uiSelectors,
} from 'flux/selectors'
import { tagPath } from 'app/routes'

function* sendDataLayerPayload(payload) {
  const targetOrigin = yield select(uiSelectors.selectPostMessageTargetOrigin)
  sendPostMessage('retailerDataLayerEvent', payload, targetOrigin)
}

function getPageNumberFromGib(flyerGib) {
  return flyerGib?.settings?.flyer_page
}


function* sendOpen({ currentFlyer, customLabel, originalTimestamp }) {
  const { id, start_date, end_date, title } = currentFlyer

  const eventPayload = {
    event: 'catalogue_opened',
    id,
    ...customLabel,
    title,
    format: 'pdf',
    validity_start: start_date ? formatDate(new Date(start_date).getTime()) : '',
    validity_end: end_date ? formatDate(new Date(end_date).getTime()) : '',
    ...originalTimestamp,
  }

  yield spawn(sendDataLayerPayload, eventPayload)
}

function* sendPageView({ currentFlyer, customLabel, page, originalTimestamp }) {
  const { id, title } = currentFlyer

  const eventPayload = {
    event: 'page_viewed',
    id,
    ...customLabel,
    title,
    format: 'pdf',
    ...eventFields.pageNumber.getPageNumber(page),
    ...eventFields.flyer.getSection(currentFlyer, page),
    ...originalTimestamp,
  }

  yield spawn(sendDataLayerPayload, eventPayload)
}

function* sendProductImpression(tagId) {
  const tag = yield select(tagsSelectors.selectTag, tagId)
  if (!tag) return

  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, tag.id)

  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer)
  const { id, title } = currentFlyer

  const pageNumber = getPageNumberFromGib(flyerGib)
  const eventPayload = {
    event: 'product_impression',
    id,
    ...eventFields.flyer.getCustomLabel(currentFlyer),
    title,
    format: 'pdf',
    ...eventFields.flyer.getSection(currentFlyer, pageNumber),
    ...eventFields.tag.getProductName(tag),
    ...eventFields.gib.getProductCode(flyerGib),
    ...eventFields.tag.getFullPrice(tag),
    ...eventFields.tag.getDiscountedPrice(tag),
    ...eventFields.date.getOriginalTimestamp(),
  }

  yield spawn(sendDataLayerPayload, eventPayload)
}

export function* trackOpenAndViewAndImpressions({ payload: { currentPages } }) {
  const shouldTrack = yield select(trackingSelectors.selectShouldTrackRetailerDataLayer)
  if (!shouldTrack) return

  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer)
  if (!currentFlyer) return

  const customLabel = eventFields.flyer.getCustomLabel(currentFlyer)
  const originalTimestamp = eventFields.date.getOriginalTimestamp()

  yield spawn(sendOpen, { currentFlyer, customLabel, originalTimestamp })

  for (const page of currentPages) {
    yield spawn(sendPageView, { currentFlyer, customLabel, page, originalTimestamp })
  }

  // wait for SET_CURRENT_FLYER_PAGES so that the flyer gibs are ready
  yield take(viewerActions.types.SET_CURRENT_FLYER_PAGES)

  const gibIds = yield select(flyerGibsSelectors.selectFlyerGibIdsByPages, currentPages)
  for (const gibId of gibIds) {
    yield spawn(sendProductImpression, gibId)
  }
}

export function* trackPageViewAndImpressions({ payload: { pages } }) {
  if (!Array.isArray(pages) || pages.length === 0) return

  const shouldTrack = yield select(trackingSelectors.selectShouldTrackRetailerDataLayer)
  if (!shouldTrack) return

  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer)
  if (!currentFlyer) return

  const customLabel = eventFields.flyer.getCustomLabel(currentFlyer)
  const originalTimestamp = eventFields.date.getOriginalTimestamp()

  for (const page of pages) {
    yield spawn(sendPageView, { currentFlyer, customLabel, page, originalTimestamp })
  }

  const gibIds = yield select(flyerGibsSelectors.selectFlyerGibIdsByPages, pages)
  for (const gibId of gibIds) {
    yield spawn(sendProductImpression, gibId)
  }
}

export function* trackProductImpression({ payload: { tagId } }) {
  const shouldTrack = yield select(trackingSelectors.selectShouldTrackRetailerDataLayer)
  if (!shouldTrack) return

  yield spawn(sendProductImpression, tagId)
}

export function* trackUpdateRouterMatch({ payload: { match } }) {
  if (match.path !== tagPath || !match.params.tagId) return

  yield spawn(trackProductDetailsOpened, match.params.tagId)
}

export function* trackSelectActiveProductVariant({ payload: { activeParentTagId, selectedVariantIndex } })  {
  if (typeof selectedVariantIndex !== 'number') return

  const { selectedTagId } = yield select(tagsSelectors.selectProductVariantIdsInfo, activeParentTagId)
  yield spawn(trackProductDetailsOpened, selectedTagId)
}

export function* trackProductDetailsOpened(tagId) {
  const shouldTrack = yield select(trackingSelectors.selectShouldTrackRetailerDataLayer)
  if (!shouldTrack) return

  const tag = yield select(tagsSelectors.selectTag, tagId)
  if (!tag) return

  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, tag.id)

  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer)
  const { id, title } = currentFlyer

  const pageNumber = getPageNumberFromGib(flyerGib)

  const eventPayload = {
    event: 'product_details_opened',
    id,
    ...eventFields.flyer.getCustomLabel(currentFlyer),
    title,
    format: 'pdf',
    ...eventFields.pageNumber.getPageNumber(pageNumber),
    ...eventFields.flyer.getSection(currentFlyer, pageNumber),
    ...eventFields.tag.getProductName(tag),
    ...eventFields.gib.getProductCode(flyerGib),
    ...eventFields.tag.getFullPrice(tag),
    ...eventFields.tag.getDiscountedPrice(tag),
    ...eventFields.date.getOriginalTimestamp(),
  }

  yield spawn(sendDataLayerPayload, eventPayload)
}

function* sendProductDetailsCta({ url = '', ctaLabel, tagId }) {
  const shouldTrack = yield select(trackingSelectors.selectShouldTrackRetailerDataLayer)
  if (!shouldTrack) return

  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer)
  const { id, title } = currentFlyer
  
  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, tagId)
  const tag = yield select(tagsSelectors.selectTag, tagId)

  const pageNumber = getPageNumberFromGib(flyerGib)

  const eventPayload = {
    event: 'product_cta_clicked',
    id,
    ...eventFields.flyer.getCustomLabel(currentFlyer),
    title,
    format: 'pdf',
    ...eventFields.pageNumber.getPageNumber(pageNumber),
    ...eventFields.flyer.getSection(currentFlyer, pageNumber),
    ...eventFields.tag.getProductName(tag),
    ...eventFields.gib.getProductCode(flyerGib),
    ...eventFields.tag.getFullPrice(tag),
    ...eventFields.tag.getDiscountedPrice(tag),
    cta_label: ctaLabel,
    url,
    ...eventFields.date.getOriginalTimestamp(),
  }

  yield spawn(sendDataLayerPayload, eventPayload)
}

export function* trackProductDetailsCta({
  payload: {
    button, tagId,
  },
}) {
  const url = button?.attributes?.href
  if (!url) return

  const ctaLabel = button?.value || ''

  yield spawn(sendProductDetailsCta, { url, ctaLabel, tagId })
}

export function* trackAddProductToCart({ payload: { tagId } }) {
  const ctaLabel = yield select(propertySelectors.selectAddToCartCtaText)

  yield spawn(sendProductDetailsCta, { ctaLabel, tagId })
}

export function* trackClickoutLinkClicked({ payload: { tagId } }) {
  const shouldTrack = yield select(trackingSelectors.selectShouldTrackRetailerDataLayer)
  if (!shouldTrack) return

  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer)
  const { id, title } = currentFlyer

  const tag = yield select(tagsSelectors.selectTag, tagId)
  const { externalUrl: url } = tag

  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, tagId)
  const pageNumber = getPageNumberFromGib(flyerGib)

  const eventPayload = {
    event: 'clickout_link_clicked',
    id,
    ...eventFields.flyer.getCustomLabel(currentFlyer),
    title,
    format: 'pdf',
    ...eventFields.pageNumber.getPageNumber(pageNumber),
    ...eventFields.flyer.getSection(currentFlyer, pageNumber),
    ...eventFields.tag.getProductName(tag),
    url,
    ...eventFields.date.getOriginalTimestamp(),
  }

  yield spawn(sendDataLayerPayload, eventPayload)
}

export default function* () {
  yield takeEvery(trackingActions.types.RETAILER_DATA_LAYER_OPEN_AND_VIEW, trackOpenAndViewAndImpressions)
  yield takeEvery(trackingActions.types.RETAILER_DATA_LAYER_PAGE_VIEW, trackPageViewAndImpressions)
  yield takeEvery(routerMatchActions.types.UPDATE_ROUTER_MATCH, trackUpdateRouterMatch)
  yield takeEvery(tagsActions.types.SET_SELECTED_VARIANT_INDEX, trackSelectActiveProductVariant)
  yield takeEvery(tagsActions.types.CLICK_BUTTON, trackProductDetailsCta)
  yield takeEvery(retailerCartActions.types.ADD_PRODUCT, trackAddProductToCart)
  yield takeEvery(trackingActions.types.PRODUCT_IMPRESSION, trackProductImpression)
  yield takeEvery(trackingActions.types.RETAILER_DATA_LAYER_CLICKOUT_LINK_CLICKED, trackClickoutLinkClicked)
}
